import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { authFetch, API } from './api';

export const SUGGESTIONS = [
  'Summarize my portfolio',
  'Which clients need attention?',
  "What's my best match right now?",
  'Compare top properties for Pinnacle',
  'Draft an email update for a client',
  'What should I focus on today?',
];

export function renderMarkdown(text) {
  if (!text) return null;
  const lines = text.split('\n');
  const blocks = [];
  let current = null;

  for (const line of lines) {
    if (/^[-*]\s+/.test(line)) {
      if (current?.type !== 'ul') {
        if (current) blocks.push(current);
        current = { type: 'ul', items: [] };
      }
      current.items.push(line.replace(/^[-*]\s+/, ''));
    } else if (/^\d+\.\s+/.test(line)) {
      if (current?.type !== 'ol') {
        if (current) blocks.push(current);
        current = { type: 'ol', items: [] };
      }
      current.items.push(line.replace(/^\d+\.\s+/, ''));
    } else if (/^#{1,6}\s+/.test(line)) {
      // Stray markdown headers: the system prompt forbids them in chat, but if
      // the model emits one anyway, render as a bold paragraph at normal size
      // rather than a giant heading. Strip the leading #s and wrap in **...**
      // so the existing inline-bold pass picks it up.
      if (current) {
        blocks.push(current);
        current = null;
      }
      const headerText = line.replace(/^#{1,6}\s+/, '').trim();
      blocks.push({ type: 'p', items: [`**${headerText}**`] });
    } else if (line.trim() === '') {
      if (current) {
        blocks.push(current);
        current = null;
      }
    } else {
      if (current?.type !== 'p') {
        if (current) blocks.push(current);
        current = { type: 'p', items: [] };
      }
      current.items.push(line);
    }
  }
  if (current) blocks.push(current);

  const renderInline = (s) => {
    const parts = [];
    const re = /\*\*([^*]+)\*\*/g;
    let last = 0;
    let m;
    while ((m = re.exec(s)) !== null) {
      if (m.index > last) parts.push(s.slice(last, m.index));
      parts.push(<strong key={parts.length}>{m[1]}</strong>);
      last = m.index + m[0].length;
    }
    if (last < s.length) parts.push(s.slice(last));
    return parts;
  };

  return blocks.map((b, i) => {
    if (b.type === 'ul') {
      return (
        <ul key={i} className="list-disc ml-5 my-2 space-y-0.5">
          {b.items.map((it, j) => (
            <li key={j}>{renderInline(it)}</li>
          ))}
        </ul>
      );
    }
    if (b.type === 'ol') {
      return (
        <ol key={i} className="list-decimal ml-5 my-2 space-y-0.5">
          {b.items.map((it, j) => (
            <li key={j}>{renderInline(it)}</li>
          ))}
        </ol>
      );
    }
    return (
      <p key={i} className="my-1">
        {b.items.map((line, j) => (
          <span key={j}>
            {renderInline(line)}
            {j < b.items.length - 1 && <br />}
          </span>
        ))}
      </p>
    );
  });
}

export default function ChatWidget() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [thinking, setThinking] = useState(false);
  const [error, setError] = useState(null);

  const bottomRef = useRef(null);
  const textareaRef = useRef(null);

  // Track mobile vs desktop via matchMedia. We can't just use Tailwind `md:` classes for
  // the sizing because the mobile dimensions live in inline `style` (they depend on
  // JS-computed visualViewport height), and inline styles outrank Tailwind utilities.
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined'
      ? window.matchMedia('(max-width: 767px)').matches
      : false
  );
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 767px)');
    const onChange = (e) => setIsMobile(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  // Panel height for mobile. Driven by visualViewport (the *visible* viewport, NOT the
  // layout viewport that 100vh/100dvh refer to). When the iOS keyboard opens,
  // visualViewport shrinks immediately and we apply the new height as an explicit pixel
  // value — far more reliable than dvh on older iOS versions.
  const [panelHeight, setPanelHeight] = useState(
    typeof window !== 'undefined' ? window.innerHeight : 600
  );
  useEffect(() => {
    if (!open) return undefined;
    const vv = window.visualViewport;
    if (!vv) {
      setPanelHeight(window.innerHeight);
      return undefined;
    }
    const update = () => setPanelHeight(vv.height);
    update();
    vv.addEventListener('resize', update);
    vv.addEventListener('scroll', update);
    return () => {
      vv.removeEventListener('resize', update);
      vv.removeEventListener('scroll', update);
    };
  }, [open]);

  useEffect(() => {
    if (bottomRef.current) {
      bottomRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }
  }, [messages, thinking, open]);

  // Lock the background page scroll while the chat is open. We need both
  // overflow:hidden AND position:fixed because iOS Safari ignores overflow:hidden on
  // the body — only position:fixed reliably stops the rubber-band scroll. We freeze
  // the body at its current scroll offset by setting top:-{scrollY}px, then restore
  // scrollY on close. Width:100% prevents the fixed-position body from collapsing.
  useEffect(() => {
    if (!open) return undefined;
    const body = document.body;
    const html = document.documentElement;
    const prev = {
      bodyOverflow: body.style.overflow,
      bodyPosition: body.style.position,
      bodyTop: body.style.top,
      bodyWidth: body.style.width,
      htmlOverflow: html.style.overflow,
    };
    const scrollY = window.scrollY || window.pageYOffset || 0;
    body.style.overflow = 'hidden';
    body.style.position = 'fixed';
    body.style.top = `-${scrollY}px`;
    body.style.width = '100%';
    html.style.overflow = 'hidden';
    return () => {
      body.style.overflow = prev.bodyOverflow;
      body.style.position = prev.bodyPosition;
      body.style.top = prev.bodyTop;
      body.style.width = prev.bodyWidth;
      html.style.overflow = prev.htmlOverflow;
      // Restore the scroll position the user was at before opening the chat.
      window.scrollTo(0, scrollY);
    };
  }, [open]);

  if (!user || !['super_admin', 'broker', 'agent'].includes(user.role)) {
    return null;
  }

  const sendMessage = async (text) => {
    const trimmed = (text || '').trim();
    if (!trimmed || thinking) return;

    const history = messages;
    const nextMessages = [...messages, { role: 'user', content: trimmed }];
    setMessages(nextMessages);
    setInput('');
    setThinking(true);
    setError(null);

    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }

    try {
      const res = await authFetch(`${API}/api/chat`, {
        method: 'POST',
        body: JSON.stringify({
          message: trimmed,
          conversation: history,
        }),
      });

      if (!res.ok) {
        throw new Error(`Request failed: ${res.status}`);
      }

      const data = await res.json();
      setMessages((m) => [
        ...m,
        { role: 'assistant', content: data.response || '' },
      ]);
    } catch (e) {
      setError('Something went wrong. Please try again.');
      setMessages((m) => [
        ...m,
        {
          role: 'assistant',
          content:
            "Sorry, I couldn't process that just now. Please try again in a moment.",
        },
      ]);
    } finally {
      setThinking(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage(input);
    }
  };

  const handleInput = (e) => {
    setInput(e.target.value);
    const el = e.target;
    el.style.height = 'auto';
    const maxHeight = 24 * 4 + 16; // approx 4 rows
    el.style.height = `${Math.min(el.scrollHeight + 2, maxHeight)}px`;
  };

  return (
    <>
      {/* Floating button */}
      {!open && (
        <button
          onClick={() => {
            // On mobile, route to a real /chat page — fixed overlays + iOS keyboards
            // are unreliable. Desktop keeps the floating-panel experience.
            if (window.innerWidth < 768) {
              navigate('/chat');
            } else {
              setOpen(true);
            }
          }}
          aria-label="Open SchemeSync AI chat"
          className="fixed bottom-6 right-6 w-14 h-14 rounded-full flex items-center justify-center text-white text-2xl shadow-lg border border-white/10 transition-all duration-200 hover:scale-105 hover:brightness-110"
          style={{ backgroundColor: '#FF6B35', zIndex: 9999 }}
        >
          <span role="img" aria-label="chat">💬</span>
        </button>
      )}

      {/* Chat panel — full-screen takeover on mobile, floating card on desktop.
          Mobile uses 100vw + 100dvh (with --vh calc() fallback) so the bottom edge
          tracks the real visible viewport — the input bar never gets clipped.
          Desktop overrides to a floating 400×600 card via md: utilities. */}
      {open && (
      <div
        className={`fixed shadow-2xl flex flex-col overflow-hidden ${
          isMobile
            ? 'top-0 left-0 rounded-none border-0'
            : 'bottom-6 right-6 w-[400px] h-[600px] max-h-[80vh] rounded-2xl border border-[#546E7A]/40'
        }`}
        style={{
          backgroundColor: '#1A2744',
          zIndex: 9999,
          touchAction: 'manipulation',
          // Defensive containment: no horizontal scrolling, no rubber-band scroll
          // bleed-through to the page below, no width creep from padding/borders.
          boxSizing: 'border-box',
          overflow: 'hidden',
          overflowX: 'hidden',
          overscrollBehavior: 'none',
          // Inline sizing only on mobile — desktop dimensions come from the Tailwind
          // classes above. On mobile, we use 100vw for width and an explicit pixel
          // height from `panelHeight` (driven by window.visualViewport above) so the
          // panel always matches the *visible* viewport — when iOS Safari's keyboard
          // opens or the URL bar expands, visualViewport shrinks and panelHeight
          // follows, keeping the input bar pinned to the visible bottom edge.
          ...(isMobile && {
            width: '100vw',
            maxWidth: '100vw',
            height: `${panelHeight}px`,
          }),
        }}
      >
        {/* Header — fixed height at top of the flex column */}
        <div
          className="flex items-center justify-between px-4 py-3 border-b shrink-0"
          style={{ backgroundColor: '#1A2744', borderColor: 'rgba(84,110,122,0.3)' }}
        >
          <div className="flex items-center gap-2">
            <span
              className="w-2.5 h-2.5 rounded-full"
              style={{ backgroundColor: '#FF6B35' }}
            />
            <div className="flex flex-col leading-tight">
              <span className="text-white font-semibold text-sm">SchemeSync AI</span>
              <span className="text-gray-500 text-xs">
                Ask anything about your portfolio
              </span>
            </div>
          </div>
          <button
            onClick={() => setOpen(false)}
            aria-label="Close chat"
            className="text-gray-400 hover:text-white leading-none flex items-center justify-center w-11 h-11 md:w-8 md:h-8 text-2xl md:text-lg shrink-0"
          >
            ✕
          </button>
        </div>

        {/* Messages — the only scrollable region inside the panel */}
        <div
          className="flex-1 overflow-y-auto overscroll-contain p-4 space-y-3"
          style={{ WebkitOverflowScrolling: 'touch', touchAction: 'pan-y' }}
        >
          {messages.length === 0 && (
            <div className="flex flex-col md:flex-row md:flex-wrap gap-2 w-full max-w-full">
              {SUGGESTIONS.map((s) => (
                <button
                  key={s}
                  onClick={() => sendMessage(s)}
                  className="w-full md:w-auto text-left md:text-center px-3 py-2 md:py-1.5 rounded-lg md:rounded-full text-xs border transition-colors hover:border-[#00D4FF] truncate min-w-0"
                  style={{
                    backgroundColor: 'rgba(10,14,39,0.7)',
                    borderColor: 'rgba(0,212,255,0.3)',
                    color: '#00D4FF',
                    maxWidth: '100%',
                  }}
                >
                  {s}
                </button>
              ))}
            </div>
          )}

          {messages.map((msg, i) =>
            msg.role === 'user' ? (
              <div
                key={i}
                className="ml-auto max-w-[85%] px-3 py-2 rounded-2xl rounded-tr-sm border text-white text-sm bg-[#FF6B35]/15 border-[#FF6B35]/30"
              >
                {msg.content}
              </div>
            ) : (
              <div
                key={i}
                className="mr-auto max-w-[90%] px-3 py-2 rounded-2xl rounded-tl-sm border text-gray-200 text-sm bg-[#0A0E27] border-[#546E7A]/30"
              >
                {renderMarkdown(msg.content)}
              </div>
            )
          )}

          {thinking && (
            <div className="mr-auto max-w-[90%] px-3 py-2 rounded-2xl rounded-tl-sm border text-gray-200 text-sm bg-[#0A0E27] border-[#546E7A]/30">
              <span className="inline-flex gap-0.5">
                <span className="animate-bounce" style={{ animationDelay: '0ms' }}>.</span>
                <span className="animate-bounce" style={{ animationDelay: '150ms' }}>.</span>
                <span className="animate-bounce" style={{ animationDelay: '300ms' }}>.</span>
              </span>
            </div>
          )}

          <div ref={bottomRef} />
        </div>

        {error && (
          <div className="px-4 py-1.5 text-xs text-red-300 bg-red-900/20 border-t border-red-500/20">
            {error}
          </div>
        )}

        {/* Input bar — re-enables touch so the textarea is usable on touch devices.
            Sticks to the bottom of the flex column; on iOS, `position: fixed` on the panel
            shrinks with the visual viewport when the keyboard appears, keeping this visible.
            min-w-0 on the textarea is critical: without it, flex-1 won't actually shrink
            below the intrinsic content size and the send button gets pushed off-screen. */}
        <div
          className="px-3 py-2 flex items-end gap-2 border-t shrink-0 w-full max-w-full"
          style={{ borderColor: 'rgba(84,110,122,0.3)', backgroundColor: '#1A2744', touchAction: 'auto', boxSizing: 'border-box' }}
        >
          <textarea
            ref={textareaRef}
            rows={1}
            value={input}
            onChange={handleInput}
            onKeyDown={handleKeyDown}
            placeholder="Ask anything..."
            className="flex-1 min-w-0 resize-none rounded-lg px-3 py-2 text-sm text-white placeholder-gray-500 border focus:outline-none focus:border-[#00D4FF]/60"
            style={{
              backgroundColor: '#0A0E27',
              borderColor: 'rgba(84,110,122,0.5)',
              maxHeight: '112px',
            }}
          />
          <button
            onClick={() => sendMessage(input)}
            disabled={!input.trim() || thinking}
            aria-label="Send"
            className="w-11 h-11 md:w-9 md:h-9 shrink-0 rounded-full md:rounded-lg flex items-center justify-center text-white text-base transition-all disabled:opacity-40 disabled:cursor-not-allowed hover:brightness-110"
            style={{ backgroundColor: '#FF6B35' }}
          >
            <span>➤</span>
          </button>
        </div>
      </div>
      )}
    </>
  );
}
